import "../../body.css";

import React, { useEffect } from "react";

function Symbols() {
    useEffect(() => {
        document.title = "Josiah Swanson - Symbols are Not Enough";
    }, []);
    return (
        <>
            <section>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;It is no small thing
                    that God speaks. If it had not pleased God to open His mouth, we would
                    be lost in darkness, groping for answers. Yet, He has revealed
                    Himself: through creation, through written word, and ultimately
                    through the incarnation of His Son. These modes of revelation are not
                    incidental—they are intentional, personal, and powerful. But in our
                    time, there is a temptation to admire the poetry while forgetting the
                    plot. This essay is written in protest of that forgetfulness.
                    Specifically, this essay addresses symbols in Scripture, and how they
                    ought never be detached from reality. Each one points to something
                    real, something grounded in history—and it is because of this that the
                    symbol has so much meaning. In what follows, I will reflect first on
                    the beauty and richness of symbols. Then, I will argue that God’s
                    authorship of Scripture gives us confidence both in its clarity and
                    historical veracity. I will finally show how this clarity and veracity
                    undergirds symbols, establishing their usefulness.
                </p>

                <h3>Symbols</h3>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Since the beginning of
                    time, God has been speaking to us through His creation. The roaring of
                    waterfalls and breakers out at sea are messengers of unparalleled
                    power, bearing the unique signature of a Creator. Seeking refuge, we
                    hear the voice of hospitality in the warmth of the sun and in peaceful
                    streams that quench our thirst.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Seeking not to be
                    misunderstood, God has been accommodating us with actual written
                    language as well. He tells us things about Himself, things we could
                    not have known without the definiteness of an alphabet. He is, “The
                    Lord, the Lord, a God merciful and gracious, slow to anger, and
                    abounding in steadfast love and faithfulness” (Ex 34:6b-7). He speaks
                    of ancient rebellions and even more ancient alliances. He surprises us
                    with secrets about our neighbors and deeper secrets about ourselves.
                    Most importantly, God has told us of a great fall and its greater
                    remedy, the Lamb of God.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;And, why a lamb? Those
                    who have read the gospel accounts know of Whom I speak: Jesus Christ,
                    the substitute for sinners. He is the centerpiece and pinnacle of all
                    God has communicated. But, again, why do we think of Him as a lamb? I
                    contend that God likes symbols. Some might argue that we could not
                    possibly know this. However, I will simply point to Christ, the
                    God-man. He often spoke of snakes in the wilderness, a great fish in
                    the deep, and the destruction of a temple. Since the coming of Christ,
                    God’s love of symbols is undisputed.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Symbols are powerful.
                    When I taste a piece of fresh sourdough, cracking under my teeth,
                    along with a sip of sweet red wine, I come to know more deeply what is
                    meant by, “Christ in me.” Likewise, Jesus’ use of ancient symbols
                    makes more clear the spiritual truths He communicates. Because I know
                    the dreadful story about the rebellious Israelites plagued by venomous
                    snakes, I am overcome with a sense of awe at Christ, the serpent on
                    the staff, the curse for us. Christ imported so much meaning into his
                    reply to Nicodemus that the rabbi may well have spent the rest of the
                    night piecing together all the implications.
                </p>

                <h3>A Word on Scripture’s Trustworthiness and Clarity</h3>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;In arguing for the
                    history behind Biblical symbols, I believe it is necessary to let the
                    reader know what I believe about the trustworthiness and clarity of
                    Scripture.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;First, I must assert
                    that God is God, and that when He sets out to communicate with people
                    He brought into being, He succeeds. The God who created language does
                    not fail to employ it well, even when He writes through human hands.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;I must also assert
                    that the Bible is the collection of all that God has put into paper
                    and ink. It is unique from other books. It is, as it claims to be, the
                    very Word of God. To suggest that it is not is to leap into
                    ambiguity—leaving one denying either God’s clarity or human
                    comprehension.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;In conclusion, I hold
                    unapologetically to the conviction that Scripture is not only
                    authoritative and infallible as God’s Word—it is also understandable.
                    Yes, there are parts that require thoughtful study. Yes, spiritual
                    truths must be spiritually discerned. Yet, anyone anywhere can pick up
                    a Bible written in their language and find “wondrous things” in it.
                </p>

                <h3>The History Behind the Symbols</h3>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;When I read of Esau’s
                    stolen birthright and of Esther’s courageous plea and of Peter’s first
                    sermon, it’s not hard to believe that things actually happened. But,
                    what of the creation account, the parting of the Red Sea, and Jonah in
                    the belly of the great fish? I will admit that it can be challenging
                    to defend the historicity of these accounts, especially in modern
                    times.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Many Christians will
                    shrug off the empirical demands of skeptics by reminding them that
                    these stories can be literally true without being historical accounts.
                    The Song of Solomon is not history, but it joyfully concurs with the
                    real experience of marriage. Nor is Ezekiel’s vision of the valley of
                    dry bones history, though it brilliantly captures the spiritual
                    reality of regeneration. Some might ask, “Why can’t we similarly treat
                    the great flood, the conquest of the Israelites, and the misadventures
                    of the judges? After all, these stories are loaded with rich symbolism
                    which deliver striking insights.” My response: Should we interpret the
                    incredible stories in these books merely symbolically simply just
                    because they are incredible? By no means!
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Firstly, I will offer
                    genre as one reason I believe that the aforementioned accounts are
                    historical. Each portion of Scripture in the Bible can be described by
                    one or more genres. The Psalms are poetic. Proverbs falls under wisdom
                    literature. Daniel is historical and apocalyptic. What about Genesis?
                    Well, Genesis must be history. Within the first few chapters of the
                    book we encounter a genealogy with names and ages. We also quickly
                    encounter references to cities and rivers and mountains, references
                    which do not seem poetical or mysterious, but grounded in history.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;The historicity of
                    these narratives does not take away from their symbolism. Instead, it
                    establishes it. The rainbow is an important symbol because there
                    really was a great flood. The Passover meal drips with meaning because
                    there really was an angel of death before the Exodus. The symbolic
                    name “Israel” carries weight because Jacob really did wrestle with
                    God.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;I observe this also in
                    Exodus, Judges, Jonah, and in many other books. These books are
                    clearly written as histories, and, in God’s sovereignty, these
                    histories establish perennial symbols for the benefit of all mankind.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Thankfully, I’m in
                    good company. Our Lord Jesus also defends the historicity of these
                    accounts. He speaks of Adam and Eve as a real couple who were bound in
                    marriage. He speaks of the Queen of Sheba as a real individual who
                    travelled to hear from King Solomon. He compares Himself to Jonah,
                    establishing historic equivalence between His burial and resurrection
                    and the incredible events recorded in that fishy story.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Finally, I offer
                    prophecy as another pillar to support my argument for history. When
                    God spoke through the prophets of a Messiah to be born from the tribe
                    of Judah, He meant what He said. Likewise with the prophesies
                    concerning Bethlehem, the persecution of the Child, and the Egyptian
                    detour. And, these are just a few of the many moments in the Messiah’s
                    life that came to pass exactly as predicted. God seems surprisingly
                    concerned with fulfilling prophecies in the most meticulous ways
                    possible.{" "}
                </p>

                <h3>A Warning</h3>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;In 1 Corinthians 15,
                    Paul is bold. He declares that if Jesus didn’t actually raise from the
                    grave, all of His disciples are fools, deserving great pity. And, he
                    was correct. Indeed, if Christ was not raised, He must not be thought
                    of us a great teacher, but a liar. If Christ was not raised, all
                    people are still dead in their sins and without hope for salvation.
                </p>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Should it be that
                    Jesus’ resurrection be the only Biblical account with a history we
                    defend? If so, why? I can understand why we should prioritize this
                    account, as it is so crucial to the Christian faith, but why should it
                    be alone? And, what are we saying to a watching world by shrugging off
                    the historical veracity of so many other Biblical narratives? Do we
                    doubt God’s power? I’m afraid it can be quite tempting to appear wise
                    in the world’s eyes in this matter, dismissing history where
                    convenient and asserting truth by standing on symbols alone. However,
                    the shaky foundation will crumble. Just look at the fifth chapter of
                    Romans and consider that Christ represents us just as Adam represented
                    us. If Adam was a fiction, then what point did Paul really make? Could
                    we really get Christ’s benefits if we didn’t get Adam’s guilt?
                </p>

                <h3>Conclusion</h3>
                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;We must not be content
                    to admire the poetry of Scripture while forgetting the plot. Symbols
                    are placed in history by God like Easter eggs in the backyard of
                    time—most of them are anchored in real events. The ark was built. The
                    sea split. The temple fell. The tomb was empty. These things actually
                    happened, and that’s what gives the symbols their power. A lamb only
                    means something because one was really slain. Bread and wine carry
                    significance today because a body was really broken and blood was
                    really poured out. If we separate symbols from the history
                    undergirding them, we’re left with sentiment and no substance. But God
                    has not given us that option. He speaks through history. He acts in
                    history. And in His providence, He’s filled history with meaning—for
                    our good, and for His glory.
                </p>
            </section>
        </>
    );
}

export default Symbols;
